/**
 * Ed25519 Key Management for Amorphous Identity
 */
import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import { base58 } from './base58.js';

// Required for ed25519 in browser/node
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m));

export interface KeyPair {
  privateKey: Uint8Array;
  publicKey: Uint8Array;
  publicKeyBase58: string;
}

/**
 * Generate a new Ed25519 keypair
 */
export function generateKeyPair(): KeyPair {
  const privateKey = ed.utils.randomPrivateKey();
  const publicKey = ed.getPublicKey(privateKey);
  const publicKeyBase58 = base58.encode(publicKey);

  return {
    privateKey,
    publicKey,
    publicKeyBase58,
  };
}

/**
 * Derive keypair from seed (32 bytes)
 */
export function keyPairFromSeed(seed: Uint8Array): KeyPair {
  if (seed.length !== 32) {
    throw new Error('Seed must be 32 bytes');
  }

  const privateKey = seed;
  const publicKey = ed.getPublicKey(privateKey);
  const publicKeyBase58 = base58.encode(publicKey);

  return {
    privateKey,
    publicKey,
    publicKeyBase58,
  };
}

/**
 * Convert public key bytes to base58 string
 */
export function publicKeyToString(publicKey: Uint8Array): string {
  return base58.encode(publicKey);
}

/**
 * Convert base58 public key string to bytes
 */
export function publicKeyFromString(str: string): Uint8Array {
  return base58.decode(str);
}

/**
 * Export keypair as JSON (for storage)
 */
export function exportKeyPair(keyPair: KeyPair): string {
  return JSON.stringify({
    privateKey: base58.encode(keyPair.privateKey),
    publicKey: keyPair.publicKeyBase58,
  });
}

/**
 * Import keypair from JSON
 */
export function importKeyPair(json: string): KeyPair {
  const data = JSON.parse(json);
  const privateKey = base58.decode(data.privateKey);
  const publicKey = ed.getPublicKey(privateKey);

  return {
    privateKey,
    publicKey,
    publicKeyBase58: data.publicKey,
  };
}
